import cl from 'classnames';
import { some } from 'lodash';
import * as React from 'react';
import { Text } from 'slate';
import { RenderElementProps } from 'slate-react';
import styled, { css } from 'styled-components';

import { colorsV2 } from '../../../../colors-v2';
import { ColorDefinition } from '../../highlight/types';

export interface StylingElementProps extends RenderElementProps {
  color: string;
  annotatedText: string;
  firstDelete: boolean;
  placeholder?: string;
  colors: ColorDefinition[];
  colorKey?: string;
  id?: string;
}

const RoundedWrapper = styled.span<{ color: string; placeholder: string }>`
  border: 1.5px solid ${(props) => props.color || colorsV2.gray20};
  box-sizing: border-box;
  border-radius: 18px;
  height: 1.5em;
  box-decoration-break: slice;
  padding: 0 6px;

  &.round-text-placeholder {
    > span:nth-child(2)::before {
      user-select: none;
      color: ${colorsV2.gray80};
      content: '${(p) => p.placeholder}';
      display: inline-block;
    }
  }
  &.round-text-deleting {
    border-style: dashed;
  }
`;

const isEmptyText = (element) =>
  Text.isText(element) && (element.text === ' ' || element.text === '');

const shouldShowPlaceholder = (element) => {
  return !some(element.children, (el) => !isEmptyText(el));
};

interface ContentWrapperProps {
  annotatedContent: any;
  annotatedBackground: any;
}

const ContentWrapper = styled.span<ContentWrapperProps>`
  position: relative;
  text-align: center;
  line-height: 2em;
  &:before {
    display: none;
    text-align: center;
    user-select: none;
    font-weight: 500;
    font-size: 10px;
    line-height: 10px;
    padding: 2px;
    width: fit-content;
    margin: 0 auto;
    margin-bottom: 4px;
    font-family: Inter, sans-serif;
    font-style: normal;
    color: ${colorsV2.white100};
    background-color: ${(props) => props.annotatedBackground};
  }
  ${(p) =>
    p.annotatedContent &&
    css`
      line-height: 1.3em;
      margin-bottom: 1em;
      &:before {
        content: '${p.annotatedContent}';
        display: block;
        margin-bottom: 8px;
      }
    `}
`;

export const RoundedElement = ({
  children,
  color,
  colors,
  colorKey,
  element,
  annotatedText,
  firstDelete,
  placeholder = 'Nhập rounded text',
  id,
}: StylingElementProps) => {
  const shouldShow = shouldShowPlaceholder(element);

  React.useEffect(() => {
    const parent = document.getElementById(id)?.parentElement?.parentElement;
    if (parent && annotatedText) {
      parent.classList.add('has-round');
    }
  }, [annotatedText, id]);

  return (
    <ContentWrapper
      id={id}
      data-rounded-annotated-text={annotatedText}
      data-rounded-color-key={colorKey}
      data-rounded-first-delete={firstDelete}
      className="rounded-text"
      style={{
        display: 'inline-block',
      }}
      annotatedContent={annotatedText}
      annotatedBackground={color || colors[0].color}
    >
      <RoundedWrapper
        color={color}
        placeholder={placeholder}
        className={cl({ 'round-text-placeholder': shouldShow, 'round-text-deleting': firstDelete })}
      >
        {children}
      </RoundedWrapper>
    </ContentWrapper>
  );
};
